import React from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button, TextField, Box, Paper, Typography, Link, Stack } from '@mui/material';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { sbcParcelApi } from '../api/sbcparcel.api'; // Adjust the import based on your project structure
import { useCustomer } from '../customer';

export const ParcelForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const apRemitFile = params.get('apRemitFile');
  const week = params.get('week');
  const apIndex = params.get('apIndex');
  const { customer } = useCustomer();
  const [FedExSettlementDate, setFedExSettlementDate] = React.useState('');
  const [FedExACHNumber, setFedExACHNumber] = React.useState('');
  const [touched, setTouched] = React.useState({ FedExSettlementDate: false, FedExACHNumber: false });
  const [ediAccountNumber, setEdiAccountNumber] = React.useState<string | null>(null);

  React.useEffect(() => {
    const fetchEdiAccountNumber = async () => {
      if (apIndex !== null && apIndex !== undefined) {
        const parcels = await sbcParcelApi.getProcessedParcels(customer.id);
        const current = parcels.find(p => p.id === id);
        if (current && Array.isArray(current.AccountsPayable)) {
          const idx = parseInt(apIndex, 10);
          setEdiAccountNumber(current.AccountsPayable[idx]?.EDIAccountNumber ?? null);
        }
      }
    };
    fetchEdiAccountNumber();
  }, [apIndex, id, customer.id]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setTouched({ FedExSettlementDate: true, FedExACHNumber: true });
    if (!FedExSettlementDate || !FedExACHNumber) return;
    if (!id) return;
    // If apIndex is present, update only the correct AccountsPayable item
    try {
      if (apIndex !== null && apIndex !== undefined) {
        // Fetch the current AccountsPayable item to preserve required fields
        const parcels = await sbcParcelApi.getProcessedParcels(customer.id);
        const current = parcels.find(p => p.id === id);
        if (!current || !Array.isArray(current.AccountsPayable)) throw new Error('Parcel or AccountsPayable not found');
        const idx = parseInt(apIndex, 10);
        const existing = current.AccountsPayable[idx] || {};
        await sbcParcelApi.updateProcessedParcel(id, {
          AccountsPayable: [{
            EDIAccountNumber: existing.EDIAccountNumber ?? null,
            AccountsPayableRemitFile: existing.AccountsPayableRemitFile ?? null,
            FedExACHNumber,
            FedExSettlementDate,
            APRemitInformationProvided: true,
            RemittanceFileURL: existing.RemittanceFileURL ?? null,
            RemittanceFileUploaded: existing.RemittanceFileUploaded ?? false
          }],
          Week: week || undefined
        });
      } else {
        // fallback: update top-level fields (legacy)
        await sbcParcelApi.updateProcessedParcel(id, {
          FedExACHNumber,
          FedExSettlementDate,
          APRemitInformationProvided: true,
          Week: week || undefined,
          AccountsPayableRemitFile: apRemitFile || undefined,
        });
      }
      navigate('/');
    } catch (err) {
      console.error('Error updating parcel:', err);}
  };

  // Format week to have forward slashes if it looks like YYYYMMDD-YYYYMMDD or MMDDYYYY
  let formattedWeek = week;
  if (week) {
    if (/^\d{8}-\d{8}$/.test(week)) {
      // Format YYYYMMDD-YYYYMMDD
      formattedWeek = `${week.slice(0,4)}/${week.slice(4,6)}/${week.slice(6,8)} - ${week.slice(9,13)}/${week.slice(13,15)}/${week.slice(15,17)}`;
    } else if (/^\d{8}$/.test(week)) {
      // Format MMDDYYYY
      formattedWeek = `${week.slice(0,2)}/${week.slice(2,4)}/${week.slice(4,8)}`;
    }
  }

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
      <Paper sx={{ p: 5, minWidth: 500, maxWidth: 700 }}>
        <Stack spacing={3}>
          <Typography variant="h6" align="center" sx={{ mb: 0, lineHeight: 1 }}>
            {customer.name}{formattedWeek ? ` - ${formattedWeek}` : ''}
          </Typography>
          {ediAccountNumber && (
            <Typography variant="subtitle1" sx={{ fontWeight: 500, mt: 0, mb: 0, lineHeight: 1 }} align="center">
              Account Number: {ediAccountNumber}
            </Typography>
          )}
          <Box>
            {apRemitFile && (
              <Link href={apRemitFile} underline="hover" sx={{ display: 'flex', alignItems: 'center', fontWeight: 500 }} target="_blank" rel="noopener noreferrer">
                <InsertDriveFileOutlinedIcon sx={{ mr: 1 }} />
                Download AP Excel File
              </Link>
            )}
          </Box>
          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <TextField
                label="FedEx Settlement Date"
                type="date"
                value={FedExSettlementDate}
                onChange={e => setFedExSettlementDate(e.target.value)}
                onBlur={() => setTouched(t => ({ ...t, FedExSettlementDate: true }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
                error={touched.FedExSettlementDate && !FedExSettlementDate}
                helperText={touched.FedExSettlementDate && !FedExSettlementDate ? 'FedEx Settlement Date is required' : ' '}
              />
              <TextField
                label="FedEx ACH Number"
                value={FedExACHNumber}
                onChange={e => setFedExACHNumber(e.target.value)}
                onBlur={() => setTouched(t => ({ ...t, FedExACHNumber: true }))}
                fullWidth
                required
                error={touched.FedExACHNumber && !FedExACHNumber}
                helperText={touched.FedExACHNumber && !FedExACHNumber ? 'FedEx ACH Number is required' : ' '}
              />
              <Button type="submit" variant="contained" fullWidth sx={{ mt: 1 }}>
                Submit
              </Button>
            </Stack>
          </form>
        </Stack>
      </Paper>
    </Box>
  );
};
